// R45: Eén gedeelde IntersectionObserver. Bij eerste keer in beeld → prefetch.
// R63: 200ms debounce op intersect zodat snelle scroll niet 50 calls tegelijk
// afvuurt richting Lightspeed (rate-limit-bescherming).
import { useEffect, useRef } from "react";
import { prefetchOrderLines } from "@/lib/orderLinesPrefetch";

let observer: IntersectionObserver | null = null;
const elToOrderid = new WeakMap<Element, number>();
const pendingTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

const PREFETCH_DEBOUNCE_MS = 200;

function getObserver() {
  if (observer) return observer;
  if (typeof IntersectionObserver === "undefined") return null;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const id = elToOrderid.get(entry.target);
        if (id == null) continue;

        if (!entry.isIntersecting) {
          // Cancel pending timer if user scrolled away within debounce window.
          const t = pendingTimers.get(entry.target);
          if (t) {
            clearTimeout(t);
            pendingTimers.delete(entry.target);
          }
          continue;
        }

        if (pendingTimers.has(entry.target)) continue;
        const timer = setTimeout(() => {
          pendingTimers.delete(entry.target);
          console.log(`[ui-prefetch R63 orderid=${id}] viewport-trigger`);
          prefetchOrderLines(id);
          try { observer!.unobserve(entry.target); } catch { /* noop */ }
        }, PREFETCH_DEBOUNCE_MS);
        pendingTimers.set(entry.target, timer);
      }
    },
    { rootMargin: "200px 0px", threshold: 0.01 },
  );
  return observer;
}

export function usePrefetchOnVisible(orderid: number | null) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || orderid == null) return;
    const obs = getObserver();
    if (!obs) {
      console.log(`[ui-prefetch R63 orderid=${orderid}] viewport-trigger (no-IO fallback)`);
      prefetchOrderLines(orderid);
      return;
    }
    elToOrderid.set(el, orderid);
    obs.observe(el);
    return () => {
      const t = pendingTimers.get(el);
      if (t) {
        clearTimeout(t);
        pendingTimers.delete(el);
      }
      try { obs.unobserve(el); } catch { /* noop */ }
    };
  }, [orderid]);
  return ref;
}
